import { Component, Emit, Prop, Vue, Watch } from "vue-property-decorator";

import { DateUtils } from "@/utils/dateUtils";
import { GroupUtils } from '@/utils/groupUtils';
import { AppConfig } from '@/app-config';
import { ApiResult, MimeType } from '@/enums';
import DriveItem from "@/components/DriveItem.vue";
import Loading from '@/components/Loading.vue';
import UploadButton from '@/components/UploadButton.vue';
import { DriveItem as mDriveItem } from '@/models/driveItem';
import { Consts } from '@/consts';
import { GraphApiUtils } from '@/utils/graphApiUtils';
import { GraphApiResult } from '@/models/graphApiResult';
import { FileUtils } from '@/utils/fileUtils';
import { DriveItems } from '@/models/driveItems';
import { Tasks } from '@/models/tasks';
import { PlannerUtils } from '@/utils/plannerUtils';
import { Plan } from '@/models/plan';
import { Bucket } from '@/models/bucket';
import { TaskPatch } from '@/models/task';
import { UploadSession } from '@/models/uploadSession';

@Component({
	components: { DriveItem, Loading, UploadButton }
})
export default class EditDriveItems extends Vue {
	@Prop() public title!: string;
	@Prop() public name!: string;
	@Prop() public dateTitle!: string;
	@Prop() public date!: Date;
	@Prop() public path!: string;
	@Prop() public mimeType!: MimeType;
	@Prop() public uploadButtonVisible!: boolean;
	@Prop() public deleteButtonVisible!: boolean;
	@Prop() public downloadButtonInvisible!: boolean;
	@Prop() public openBrowserButtonInvisible!: boolean;
	@Prop() public userName!: string;
	@Prop() public isVisibleUser!: boolean;
	@Prop() public plan!: Plan;
	@Prop() public bucket!: Bucket;
	private groupId: string = '';
	private driveItems: DriveItems = new DriveItems();
	private driveItemsLoading: boolean = false;
	private uploadButtonDisabled: boolean = false;
	private uploadButtonUploading: boolean = false;
	private message: string = 'ファイルがありません';
	private isVisibleMessage: boolean = false;
	private request: number = -1;
	public async created(): Promise<void> {
		// アクセストークンが有効でない場合はルートに遷移。
		if (!await GraphApiUtils.isValidAccessToken(Vue.$accessTokenInfo)) {
			this.$router.push(Consts.PATH_USER);
			return;
		}
		const accessToken: string = Vue.$accessTokenInfo.token;
		const groupId: string | null = localStorage.getItem(Consts.LOCAL_STORAGE_KEY_USER_GROUPID);
		if (groupId === null) {
			return;
		}
		this.groupId = groupId;
		await this.updateDriveItems(accessToken, this.groupId, this.path, this.mimeType);
	}

	@Emit("drive-items-close")
	public close(): void { }

	public async updateDriveItems(
		accessToken: string,
		groupId: string,
		path: string,
		mimeType?: MimeType
	): Promise<void> {
		try {
			this.driveItemsLoading = true;
			this.driveItems.value = [];
			const driveItemsResult = await GroupUtils.GetDriveItemsByPath(accessToken, groupId, path);
			if ((driveItemsResult.result !== ApiResult.Success) || (driveItemsResult.value === null)) {
				alert('ファイル一覧の取得に失敗しました');
				return;
			}
			const driveItems = driveItemsResult.value;
			const items: mDriveItem[] = [];
			for (const driveItem of driveItems.value) {
				if (driveItem.file === undefined) {
					// ファイルではない場合
					continue;
				}
				const file = driveItem.file;
				switch (mimeType) {
					case MimeType.Video: {
						if (file.mimeType.indexOf('video') > -1) {
							items.push(driveItem);
						}
						break;
					}
					case MimeType.NotVideo: {
						if (file.mimeType.indexOf('video') < 0) {
							items.push(driveItem);
						}
						break;
					}
					default: {
						items.push(driveItem);
					}
				}
			}
			this.driveItems.value = items;
			this.isVisibleMessage = (items.length > 0) ? false : true;
		} finally {
			this.driveItemsLoading = false;
		}
	}

	public async postDriveItem(accessToken: string, groupId: string, path: string, file: File): Promise<void> {
		try {
			this.uploadButtonUploading = true;
			this.uploadButtonDisabled = true;
			// アップロード先のDriveItem一覧を取得する
			const driveItemsResult = await GroupUtils.GetDriveItemsByPath(accessToken, groupId, path);
			if ((driveItemsResult.result !== ApiResult.Success) || (driveItemsResult.value === null)) {
				alert('ファイル一覧の取得に失敗しました');
				return;
			}
			const driveItems = driveItemsResult.value;
			// ファイル名が重複しないようにする
			const increamentName: string = driveItems.increamentName(file.name);
			// ファイルをアップロードする
			if (file.size < Consts.GRAPH_API_UPLOAD_MAX_FILESIZE) {
				// 4MBより小さい場合
				const driveItemResult = await GroupUtils.PostDriveItemByPath(accessToken, groupId, path, increamentName, file);
				if ((driveItemResult.result !== ApiResult.Success) || (driveItemResult.value === null)) {
					// エラー時の処理
					return;
				}
			} else {
				const filePath: string = `${path}/${increamentName}`;
				const uploadSessionResult = await GroupUtils.PostUploadSession(accessToken, groupId, filePath);
				if ((uploadSessionResult.result !== ApiResult.Success) || (uploadSessionResult.value === null)) {
					return;
				}
				const uploadUrl: string = uploadSessionResult.value.uploadUrl;
				const driveItemResult = await GroupUtils.UploadFile(uploadUrl, file);
				if ((driveItemResult.result !== ApiResult.Success) || (driveItemResult.value === null)) {
					// エラー時の処理
					return;
				}
			}
		} finally {
			this.uploadButtonUploading = false;
			this.uploadButtonDisabled = false;
		}
	}

	private getDateString(date: Date): string {
		const dateString: string = DateUtils.GetStringWithHyphen(date);
		return dateString;
	}

	private onCloseClick(): void {
		this.close();
	}

	private async onDeleteDriveItem(driveItemId: string): Promise<void> {
		const accessToken: string = Vue.$accessTokenInfo.token;
		const deleteResult: GraphApiResult<void> = await GroupUtils.DelteDriveItem(accessToken, this.groupId, driveItemId);
		if (deleteResult.result !== ApiResult.Success) {
			alert('ファイルの削除に失敗しました');
			return;
		}
		// ファイルリストを更新する
		await this.updateDriveItems(accessToken, this.groupId, this.path);
		// タスクを更新する
		if ((this.plan !== undefined) && (this.bucket !== undefined) && (this.driveItems.value.length === 0)) {
			// レポート提出タスクを開始前にする
			const taskPatch: TaskPatch = new TaskPatch();
			taskPatch.percentComplete = 0;
			await this.updateTask(accessToken, this.plan, this.bucket, taskPatch);
		}
	}

	private async onUploadButtonInputChange(files: FileList, uploadPath: string): Promise<void> {
		const file: File | null = files.item(0);
		if ((file === null) || (uploadPath === undefined)) {
			return;
		}
		const accessToken: string = Vue.$accessTokenInfo.token;
		// ファイルをアップロードする
		await this.postDriveItem(accessToken, this.groupId, uploadPath, file);
		// ファイルリストを更新する
		await this.updateDriveItems(accessToken, this.groupId, uploadPath);
		// タスクを更新する
		if ((this.plan !== undefined) && (this.bucket !== undefined)) {
			// レポート提出タスクを完了済にする
			const taskPatch: TaskPatch = new TaskPatch();
			taskPatch.percentComplete = 100;
			await this.updateTask(accessToken, this.plan, this.bucket, taskPatch);
		}
	}

	private async updateTask(accessToken: string, plan: Plan, bucket: Bucket, taskPatch: TaskPatch): Promise<void> {
		const tasks: Tasks | null = await this.getTasks(accessToken, plan.id);
		if (tasks === null) {
			return;
		}
		const bucketTasks: Tasks = tasks.getBucketTasks(plan.id, bucket.id);
		switch (this.bucket.name) {
			case AppConfig.BUCKET_NAME_PRETASK:
			case AppConfig.BUCKET_NAME_PRETASK2:
			case AppConfig.BUCKET_NAME_PRETASK3:
			case AppConfig.BUCKET_NAME_POSTTASK: {
				if (!Vue.$isAdmin) {
					const userId: string | null = localStorage.getItem(Consts.LOCAL_STORAGE_KEY_USER_ID);
					if (userId !== null) {
						const myTasks = bucketTasks.getMyTasks(userId);
						// レポート提出タスクを更新する
						const task = myTasks.getByTitle(AppConfig.USER_TASK_NAME_SUBMIT_REPORT);
						if (task !== null) {
							await PlannerUtils.PatchTask(accessToken, task.id, taskPatch, task.etag);
						}
					}
				} else {
				}
			}
		}
	}

	private async getTasks(accessToken: string, planId: string): Promise<Tasks | null> {
		const tasksResult: GraphApiResult<Tasks> = await PlannerUtils.GetTasks(accessToken, planId);
		if ((tasksResult.result !== ApiResult.Success) || (tasksResult.value === null)) {
			alert('タスクの取得に失敗しました。');
			return null;
		}
		const tasks: Tasks = tasksResult.value;
		return tasks;
	}
}
